import {
  OUTLOOK_TRIGGER_FOLDERS,
  type OutlookTriggerDraft,
  type OutlookTriggerFolder,
} from '@littlebird/sdk';
import { useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Popover } from '@/components/ui/popover';

import {
  outlookDirectionLabel,
  outlookFilterFields,
  setOutlookFilters,
} from '../outlook-trigger-filters';

import { RoutineMeetingFilterInput } from './routine-meeting-filter-input';

import type { GmailAccountOption } from './routine-gmail-trigger-row';

const FOLDER_OPTIONS: { label: string; folder: OutlookTriggerFolder }[] = [
  { label: 'Received', folder: 'inbox' },
  { label: 'Sent', folder: 'sentitems' },
];

export function RoutineOutlookTriggerRow({
  trigger,
  outlookAccounts,
  onConfigure,
  configurationDisabled,
  editingDisabled,
  onChange,
  onRemove,
}: {
  trigger: OutlookTriggerDraft;
  outlookAccounts: GmailAccountOption[];
  onConfigure: () => void;
  configurationDisabled: boolean;
  editingDisabled: boolean;
  onChange: (trigger: OutlookTriggerDraft) => void;
  onRemove: () => void;
}) {
  const mailbox = trigger.connectedAccountId
    ? outlookAccounts.find(
        (account) => account.id === trigger.connectedAccountId
      )?.account_label || 'Connected Outlook account'
    : 'Choose account';
  const direction = outlookDirectionLabel(trigger.folders);
  const setFilters = setOutlookFilters(trigger);
  const filtersRef = useRef<HTMLDivElement>(null);

  return (
    <YStack
      role="group"
      aria-label="Outlook email trigger"
      className="gap-2 rounded-xl border border-border bg-surface px-3 py-2"
    >
      <XStack className="min-h-10 items-center gap-2">
        <XStack
          center
          className="size-8 shrink-0 rounded-full border border-border bg-surface"
        >
          <Icons.outlook className="size-4" />
        </XStack>
        <XStack className="min-w-0 flex-1 flex-wrap items-baseline gap-2 text-sm">
          <span>New</span>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button
                aria-label={`Outlook mail: ${direction}`}
                variant="outline"
                size="sm"
                className="shrink-0 gap-1.5 px-2"
                disabled={editingDisabled}
              >
                {direction}
                <Icons.chevronDown className="size-3.5 shrink-0" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="start" className="w-40">
              {FOLDER_OPTIONS.map((option) => (
                <DropdownMenu.CheckboxItem
                  key={option.folder}
                  checked={trigger.folders.includes(option.folder)}
                  disabled={
                    trigger.folders.length === 1 &&
                    trigger.folders.includes(option.folder)
                  }
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(checked) =>
                    onChange({
                      ...trigger,
                      folders: OUTLOOK_TRIGGER_FOLDERS.filter((folder) =>
                        folder === option.folder
                          ? checked === true
                          : trigger.folders.includes(folder)
                      ),
                    })
                  }
                >
                  {option.label}
                </DropdownMenu.CheckboxItem>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
          <span>email in</span>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button
                aria-label={`Outlook account: ${mailbox}`}
                variant="outline"
                size="sm"
                className="min-w-0 max-w-full gap-1.5 px-2"
                disabled={editingDisabled || outlookAccounts.length === 0}
              >
                <span className="truncate">{mailbox}</span>
                <Icons.chevronDown className="size-3.5 shrink-0" />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="start" className="w-64">
              <DropdownMenu.RadioGroup
                value={trigger.connectedAccountId ?? ''}
                onValueChange={(connectedAccountId) =>
                  onChange({ ...trigger, connectedAccountId })
                }
              >
                {outlookAccounts.map((account) => (
                  <DropdownMenu.RadioItem key={account.id} value={account.id}>
                    {account.account_label || 'Connected Outlook account'}
                  </DropdownMenu.RadioItem>
                ))}
              </DropdownMenu.RadioGroup>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </XStack>
        <Popover.Root
          onOpenChange={(open) => {
            // Closing unmounts the inputs without a blur, which is when they commit typed text
            const focused = document.activeElement;
            if (
              !open &&
              focused instanceof HTMLElement &&
              filtersRef.current?.contains(focused)
            ) {
              focused.blur();
            }
          }}
        >
          <Popover.Trigger asChild>
            <Button
              aria-label="Outlook filters"
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5"
              disabled={editingDisabled}
            >
              <Icons.slidersHorizontal className="size-3.5" />
              {setFilters.length > 0
                ? `Filters (${setFilters.length})`
                : 'Filters'}
            </Button>
          </Popover.Trigger>
          <Popover.Content
            ref={filtersRef}
            align="end"
            className="w-[30rem] p-3"
          >
            <YStack className="gap-2">
              {outlookFilterFields(trigger.folders).map((field) => (
                <RoutineMeetingFilterInput
                  key={field.key}
                  label={field.label}
                  placeholder={field.placeholder}
                  values={trigger[field.key]}
                  separator={field.separator}
                  onValuesChange={(values) =>
                    onChange({ ...trigger, [field.key]: values })
                  }
                />
              ))}
            </YStack>
          </Popover.Content>
        </Popover.Root>
        <Button
          variant="outline"
          size="sm"
          className="shrink-0"
          disabled={configurationDisabled}
          onClick={onConfigure}
        >
          <Icons.sparkle className="size-3.5" />
          Configure with Littlebird
        </Button>
        <Button
          aria-label="Remove Outlook email trigger"
          variant="ghost"
          size="icon-sm"
          iconOnly
          className="shrink-0 text-foreground-muted"
          onClick={onRemove}
          disabled={editingDisabled}
        >
          <Icons.trash2 />
        </Button>
      </XStack>
      <XStack className="min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1 pl-10 text-sm">
        {setFilters.length === 0 ? (
          <span className="text-foreground-muted">Every new email</span>
        ) : (
          setFilters.map(({ label, values }) => (
            <span key={label} className="min-w-0 max-w-full leading-7">
              <span className="text-foreground-muted">{label} </span>
              {values.map((value) => (
                <span
                  key={value}
                  className="mr-1 inline-block max-w-full rounded-xl border border-grayA-5 bg-grayA-3 px-2.5 py-0.5 align-baseline font-medium leading-5 text-foreground [overflow-wrap:anywhere]"
                >
                  {value}
                </span>
              ))}
            </span>
          ))
        )}
      </XStack>
      {outlookAccounts.length === 0 && (
        <p className="text-xs text-foreground-muted">
          Connect an Outlook account in Settings to use this trigger.
        </p>
      )}
    </YStack>
  );
}
